import { useEffect, useRef, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { RootState } from "../../../store/root-reducer";
import { isScopeLoading } from "../../../store/scope-loading/helpers";
import TableGrid from "../../../components/TableGrid";
import ContractViewModal from "./Modals/PendingContractViewModal";
import { getPendingContractRecords } from "../../../store/user/actions";
import { PendingContractStatus, UserScope } from "../../../store/user/types";
import { formatDate } from "../../../helper";
import { CONTRACT_RECORD_TYPE } from "../../../constant";

const statusbg = {
  [PendingContractStatus.Pending]:
    "bg-yellow-200 text-yellow-600 border border-yellow-600",
  [PendingContractStatus.Issue]:
    "bg-red-200 text-red-600 border border-red-600",
  [PendingContractStatus.Processing]:
    "bg-blue-200 text-blue-600 border border-blue-600",
  [PendingContractStatus.Processed]:
    "bg-green-200 text-green-600 border border-green-600",
};

function PendingContracts({ data, loading, info, associated_dealers }: any) {
  const dispatch = useDispatch();
  const searchDataRef = useRef(null);

  const [isOpen, setIsOpen] = useState({
    show: false,
    data: {},
  });

  function close() {
    setIsOpen({ show: false, data: {} });
  }

  useEffect(() => {
    if (data?.length === 0) {
      dispatch(getPendingContractRecords({ page: 1 }));
    }
    return () => {};
  }, [data?.length, dispatch]);

  const loadData = (
    page: number = 1,
    per_page: number = 20,
    values?: any,
    clearSearch: boolean = false
  ) => {
    const searchPayload = values
      ? Object.keys(values).reduce((acc, key) => {
          if (key === "dealer_id" && values[key] === "all") {
            acc[key] = associated_dealers.map((d: any) => d.id).join(",");
          } else if (values[key]) {
            acc[key] = values[key];
          }
          return acc;
        }, {})
      : null;
    if (searchPayload) searchDataRef.current = searchPayload;
    if (clearSearch) searchDataRef.current = null;
    dispatch(
      getPendingContractRecords({
        search: searchPayload || searchDataRef.current,
        page,
        per_page,
      })
    );
  };

  const tableHeaders = [
    { label: "Customer Name", key: "customerName" },
    { label: "Invoice", key: "invoice" },
    {
      label: "Status",
      key: "status",
      format: (val: any) => (
        <div
          className={`inline-block items-center justify-center  py-0.5 px-3 rounded-2xl text-[12px] ${statusbg[val]}`}
          role="presentation"
        >
          <span>{val || "N/A"}</span>
        </div>
      ),
    },
    {
      label: "Record Type",
      key: "recordType",
      format: (val: any) =>
        CONTRACT_RECORD_TYPE.find((item) => item.value === val)?.label || "N/A",
    },
    {
      label: "Purchase Date",
      key: "purchaseDate",
      format: (val: any) => formatDate(val, "N/A"),
    },
    { label: "Customer Address", key: "customerAddress" },
    { label: "Store Name", key: "storeName" },
    { label: "Sales Person", key: "salesPerson" },
  ];

  if (associated_dealers.length > 1) {
    tableHeaders.splice(1, 0, {
      label: "Associated dealer",
      key: "dealer_id",
      format: (val: any) =>
        associated_dealers.find((d: any) => d.id === val)?.alias || val,
    });
  }

  return (
    <div className="container mb-10">
      <TableGrid
        data={data}
        loading={loading}
        onPerPageChange={(per_page) => loadData(1, per_page)}
        handleViewButtonClick={(data) => setIsOpen({ show: true, data })}
        loadMoreData={(per_page) => {
          const nextPage = info?.page + 1;
          if (info?.total_pages >= nextPage) {
            loadData(nextPage, per_page);
          }
        }}
        searchComponentProps={{
          onSearchSubmit: (values, perPage) => loadData(1, perPage, values),
          onClearFilter: (perPage) => loadData(1, perPage, null, true),
          searchFields: [
            ...(associated_dealers.length > 1
              ? ([
                  {
                    label: "Associated dealer",
                    name: "dealer_id",
                    type: "select",
                    defaultValue: { label: "All", value: "all" },
                    options: [
                      { label: "All", value: "all" },
                      ...associated_dealers.map((d: any) => ({
                        label: d.alias,
                        value: d.id,
                      })),
                    ],
                  },
                ] as any)
              : []),
            {
              label: "Contract Number",
              name: "contract_id",
              type: "text",
            },
            {
              label: "Customer First Name",
              name: "customer_first_name",
              type: "text",
            },
            {
              label: "Customer Last Name",
              name: "customer_last_name",
              type: "text",
            },
            {
              label: "Customer Phone",
              name: "customer_phone",
              type: "phone",
            },
            {
              label: "Customer Email",
              name: "customer_email",
              type: "email",
            },
            {
              label: "Store Location",
              name: "selling_store_location",
              type: "text",
            },
            {
              label: "Invoice",
              name: "invoice_number",
              type: "text",
            },
            {
              label: "Purchase Date",
              name: "contract_purchase_date",
              type: "date",
            },
          ],
        }}
        headers={tableHeaders}
      />
      <ContractViewModal
        data={isOpen.data}
        isOpen={isOpen.show}
        close={close}
      />
    </div>
  );
}

const mapStateProps = (state: RootState) => {
  return {
    data: state.user.pendingContracts?.data || [],
    info: state.user.pendingContracts?.info || {},
    loading: isScopeLoading(
      state.scopeLoading,
      `${UserScope}-pendingContracts`
    ),
    associated_dealers: state.user.selectedOrg.associated_dealers || [],
  };
};

export default connect(mapStateProps)(PendingContracts);
